import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Music, Loader2, Play, Pause, SkipForward, SkipBack, ExternalLink,
  Search, X, ListMusic, Clock, Heart, TrendingUp, RefreshCw, Crown, Unplug,
} from "lucide-react";
import { useMusic } from "../context/MusicContext";

const TABS = [
  { key: "playlists", label: "Playlists", icon: ListMusic },
  { key: "liked",     label: "Liked",      icon: Heart },
  { key: "top",       label: "Top tracks", icon: TrendingUp },
  { key: "recent",    label: "Recent",     icon: Clock },
];

const fmtDuration = (ms) => {
  if (!ms) return "";
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, "0")}`;
};

export default function MusicPage() {
  // Shared music state — picking a playlist here drives the SAME iframe shown
  // by the floating pill, so it stays in sync.
  const {
    status, refreshStatus, current,
    playItem: ctxPlayItem, sendControl, connect, setPillOpen,
  } = useMusic();

  const [tab, setTab] = useState("playlists");
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const loadTabItems = useCallback(async () => {
    if (!status?.connected) return;
    setLoadingItems(true);
    try {
      const path = {
        playlists: "/spotify/playlists?limit=30",
        liked:     "/spotify/liked?limit=30",
        top:       "/spotify/top-tracks?limit=30",
        recent:    "/spotify/recently-played?limit=30",
      }[tab];
      const { data } = await api.get(path);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load");
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, [status?.connected, tab]);

  useEffect(() => { loadTabItems(); }, [loadTabItems]);

  const disconnect = async () => {
    if (!window.confirm("Disconnect Spotify? You can reconnect any time.")) return;
    try {
      await api.delete("/spotify/disconnect");
      toast.success("Spotify disconnected");
      await refreshStatus();
      setItems([]);
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const playItem = async (item, kind) => {
    // Drives the persistent iframe in the pill — opens the pill so user
    // sees what's playing without leaving this page.
    await ctxPlayItem(item, kind);
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/spotify/search?q=${encodeURIComponent(search)}&limit=10`);
      setSearchResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  // --- Render ---

  if (status === null) {
    return (
      <div className="p-10 flex items-center gap-2 text-white/50">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading music…
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto fade-up" data-testid="music-not-configured">
        <div className="label-eyebrow text-[#FFB020] mb-2">Spotify integration</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Not configured yet.</h1>
        <p className="text-white/60 mb-6">
          The admin needs to set <code className="text-[#6B9BFF]">SPOTIFY_CLIENT_ID</code>,
          <code className="text-[#6B9BFF] mx-1">SPOTIFY_CLIENT_SECRET</code>, and
          <code className="text-[#6B9BFF] mx-1">SPOTIFY_REDIRECT_URI</code> in the backend
          environment, then restart the API.
        </p>
        <ol className="text-sm text-white/70 list-decimal list-inside space-y-1 border border-white/10 bg-[#121212] p-5">
          <li>Create an app at <a className="text-[#6B9BFF] underline" href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer">developer.spotify.com/dashboard</a></li>
          <li>Add the redirect URI: <code className="text-[#6B9BFF]">{window.location.origin.replace(":3000","")}/api/spotify/callback</code></li>
          <li>Copy the Client ID & Secret into the backend .env</li>
          <li>Restart the API; refresh this page</li>
        </ol>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto fade-up" data-testid="music-connect-cta">
        <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
          <Music className="w-3.5 h-3.5" /> Spotify
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">Hook up your soundtrack.</h1>
        <p className="text-white/60 mb-8 max-w-lg">
          Link your Spotify account to browse your playlists, liked songs and top tracks
          right from your workout dashboard. Premium accounts get full playback control.
        </p>
        <button
          onClick={connect}
          className="bg-[#1DB954] text-black font-black uppercase tracking-widest px-8 py-4 hover:bg-white inline-flex items-center gap-3"
          data-testid="music-page-connect-btn"
        >
          <Music className="w-5 h-5" /> Connect Spotify
        </button>

        {/* Even without OAuth, if the user has a default playlist URL set via
            the pill's "Add URL" flow, show it here so they can find/open it. */}
        {current && (
          <div className="mt-10 border border-white/10 bg-[#121212] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="music-now-playing">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-[#1DB954]/10 border border-[#1DB954]/40 flex items-center justify-center shrink-0">
                <Music className="w-4 h-4 text-[#1DB954]" />
              </div>
              <div className="min-w-0">
                <div className="label-eyebrow text-white/40 text-[10px]">Currently loaded</div>
                <div className="font-bold text-sm truncate text-white">
                  {current.kind === "playlist" ? "Playlist" : current.kind === "album" ? "Album" : "Track"} · {current.id}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  Open the in-app player to control playback.
                </div>
              </div>
            </div>
            <button
              onClick={() => setPillOpen(true)}
              className="border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] uppercase tracking-wider text-xs px-3 py-2 inline-flex items-center gap-1.5"
              data-testid="music-open-pill"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Show player
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="music-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
            <Music className="w-3.5 h-3.5" /> Music
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">
            {status.profile?.display_name || "Your library"}.
          </h1>
          <div className="flex items-center gap-2 mt-3 text-xs text-white/50">
            {status.premium ? (
              <span className="inline-flex items-center gap-1 text-[#FFB020] border border-[#FFB020]/40 px-2 py-1">
                <Crown className="w-3 h-3" /> Premium · full control
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 border border-white/10 px-2 py-1">
                Free · browse only
              </span>
            )}
            <button
              onClick={disconnect}
              className="ml-2 inline-flex items-center gap-1 text-white/40 hover:text-[#FF3B30]"
              data-testid="spotify-disconnect"
            >
              <Unplug className="w-3 h-3" /> Disconnect
            </button>
          </div>
        </div>
        <button
          onClick={loadTabItems}
          className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 text-white/60 hover:text-[#6B9BFF]"
          data-testid="music-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Search */}
      <form onSubmit={runSearch} className="flex items-center gap-2 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Spotify playlists & tracks…"
            className="w-full pl-10 pr-10 py-3 bg-[#121212] border border-white/10 focus:outline-none focus:border-[#6B9BFF] text-sm"
            data-testid="music-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); setSearchResults(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={searching}
          className="bg-[#6B9BFF] text-black font-bold uppercase tracking-wider text-xs px-5 py-3 hover:bg-white disabled:opacity-50"
          data-testid="music-search-btn"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
      </form>

      {/* Now playing summary — the actual iframe lives in the persistent pill */}
      {current && (
        <div className="mb-8 border border-white/10 bg-[#121212] p-4 flex items-center justify-between flex-wrap gap-3" data-testid="music-now-playing">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#1DB954]/10 border border-[#1DB954]/40 flex items-center justify-center shrink-0">
              <Music className="w-4 h-4 text-[#1DB954]" />
            </div>
            <div className="min-w-0">
              <div className="label-eyebrow text-white/40 text-[10px]">Now loaded</div>
              <div className="font-bold text-sm truncate text-white">
                {current.kind === "playlist" ? "Playlist" : current.kind === "album" ? "Album" : "Track"} · {current.id}
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">
                Playing in the floating Music pill — keeps going as you navigate.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {status.premium && (
              <>
                <button onClick={() => sendControl("previous")} className="p-2 hover:text-[#6B9BFF]" data-testid="music-ctrl-prev"><SkipBack className="w-4 h-4" /></button>
                <button onClick={() => sendControl("pause")} className="p-2 hover:text-[#6B9BFF]" data-testid="music-ctrl-pause"><Pause className="w-4 h-4" /></button>
                <button onClick={() => sendControl("next")} className="p-2 hover:text-[#6B9BFF]" data-testid="music-ctrl-next"><SkipForward className="w-4 h-4" /></button>
              </>
            )}
            <button
              onClick={() => setPillOpen(true)}
              className="border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] uppercase tracking-wider text-xs px-3 py-2 inline-flex items-center gap-1.5"
              data-testid="music-open-pill"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Show player
            </button>
          </div>
        </div>
      )}

      {/* Search results */}
      {searchResults && (
        <section className="mb-10" data-testid="search-results">
          <h2 className="label-eyebrow text-white/60 mb-3">Search results</h2>
          {searchResults.playlists?.length > 0 && (
            <>
              <div className="text-xs text-white/40 mb-2 uppercase tracking-wider">Playlists</div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
                {searchResults.playlists.map((p) => (
                  <PlaylistCard key={p.id} item={p} onPlay={() => playItem(p, "playlist")} />
                ))}
              </div>
            </>
          )}
          {searchResults.tracks?.length > 0 && (
            <>
              <div className="text-xs text-white/40 mb-2 uppercase tracking-wider">Tracks</div>
              <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">
                {searchResults.tracks.map((t) => (
                  <TrackRow key={t.id} track={t} onPlay={() => playItem(t, "track")} canControl={!!status.premium} />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Tabs */}
      <div className="border-b border-white/10 mb-6 flex flex-wrap gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors inline-flex items-center gap-1.5 -mb-px ${
                tab === t.key
                  ? "border-[#6B9BFF] text-white"
                  : "border-transparent text-white/40 hover:text-white"
              }`}
              data-testid={`music-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {loadingItems ? (
        <div className="py-16 flex items-center gap-2 text-white/50">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-white/40 text-sm italic" data-testid="music-empty">
          Nothing here yet.
        </div>
      ) : tab === "playlists" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="playlist-grid">
          {items.map((p) => (
            <PlaylistCard key={p.id} item={p} onPlay={() => playItem(p, "playlist")} />
          ))}
        </div>
      ) : (
        <div className="border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid="track-list">
          {items.map((t) => (
            <TrackRow
              key={`${t.id}-${t.played_at || t.added_at || ""}`}
              track={t}
              onPlay={() => playItem(t, "track")}
              canControl={!!status.premium}
              meta={tab === "recent" ? new Date(t.played_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlaylistCard({ item, onPlay }) {
  return (
    <button
      onClick={onPlay}
      className="group text-left bg-[#121212] border border-white/10 hover:border-[#6B9BFF] transition-colors p-3"
      data-testid={`playlist-card-${item.id}`}
    >
      <div className="aspect-square bg-white/5 mb-3 overflow-hidden relative">
        {item.image ? (
          <img src={item.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30"><Music className="w-8 h-8" /></div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
          <div className="w-12 h-12 bg-[#1DB954] flex items-center justify-center rounded-full">
            <Play className="w-5 h-5 text-black ml-0.5" />
          </div>
        </div>
      </div>
      <div className="font-bold text-sm leading-tight line-clamp-1 text-white">{item.name}</div>
      <div className="text-xs text-white/50 mt-1 line-clamp-1">
        {item.owner || "Playlist"}
        {item.tracks_total ? <> · {item.tracks_total} tracks</> : null}
      </div>
    </button>
  );
}

function TrackRow({ track, onPlay, canControl, meta }) {
  return (
    <div className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors group" data-testid={`track-row-${track.id}`}>
      <button onClick={onPlay} className="w-12 h-12 shrink-0 relative bg-white/5 overflow-hidden">
        {track.image ? (
          <img src={track.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <Music className="w-5 h-5 text-white/30 m-auto absolute inset-0" />
        )}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center">
          {canControl ? <Play className="w-4 h-4 text-[#1DB954]" /> : <ExternalLink className="w-4 h-4 text-white" />}
        </div>
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold truncate text-white">{track.name}</div>
        <div className="text-xs text-white/50 truncate">
          {(track.artists || []).join(", ")}
          {track.album ? <> · {track.album}</> : null}
        </div>
        {meta && <div className="text-[10px] text-white/30 mt-0.5">{meta}</div>}
      </div>
      <div className="text-xs text-white/40 tabular-nums hidden md:block">
        {fmtDuration(track.duration_ms)}
      </div>
      <a
        href={track.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-white/30 hover:text-[#1DB954] p-2"
        onClick={(e) => e.stopPropagation()}
        title="Open in Spotify"
      >
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}
